import { EnvironmentOutlined, EyeOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import TcShowLocationModal from 'components/UI/Map/showLocationModal/TcShowLocationModal';
import TcTooltip from 'components/UI/Tooltip/TcTooltip';
import OrderAddressPicker from 'components/pages/dashboard/ParcelOrder/components/orderAddressPicker/OrderAddressPicker';
import endpointUrls from 'global/Constants/endpointUrls';
import { peopleAddress } from 'global/Models/addressModel';
import { client } from 'global/Models/clientModel';
import { IBackendSearchGeneric } from 'global/Models/globalModels';
import { lockerInfo, packageCreate } from 'global/Models/omOrderModel';
import { handleApiThenGeneric } from 'global/helperFunctions/handleApiThen';
import ApiService from 'global/utils/ApiService';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setOmOrderReservedByLocker } from 'redux/reducer/ParcelOrder/parcelOrderReducer';
import { RootState } from 'redux/store';
import OrderSelectLocker from './OrderSelectLocker';

interface IOrderAddressBox {
  address?: peopleAddress;
  setAddress: (_addess?: peopleAddress) => void;
  peopleAddressTypeId: 1 | 2;
  packages: packageCreate[];
  isNormalOrder: boolean;
  index: number;
  setLockerAddress?: (_locker?: lockerInfo) => void;
  lockerAddress?: lockerInfo;
  client?: client;
}

const OrderAddressBox: FC<IOrderAddressBox> = ({ address, setAddress, peopleAddressTypeId, packages, index, setLockerAddress, lockerAddress, client, isNormalOrder }) => {
  //states
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: string; lng: string }>();
  const [showLockerReservation, setShowLockerReservation] = useState<number>();
  const { userProfile } = useSelector((state: RootState) => state.user);

  // hooks
  const { pathname } = useLocation();
  const useLocker = pathname.includes('locker');
  const dispatch = useDispatch();

  //effect
  useEffect(() => {
    userProfile && !address && peopleAddressTypeId === 1 && !lockerAddress && getDefaultAddress();
  }, [userProfile, client]);

  //function
  const toggleModal = useCallback(() => setShowModal((prevState) => !prevState), []);
  const handleDelete = useCallback(() => setAddress(undefined), []);

  const handleDeleteLockerAddress =
    setLockerAddress &&
    useCallback(() => {
      setLockerAddress(undefined);
      dispatch(setOmOrderReservedByLocker(undefined));
    }, []);

  const getDefaultAddress = async () => {
    await ApiService.post(endpointUrls.addressGetAll, { isDefault: true, peopleAddressTypeId, creatorId: userProfile?.id })
      .then((res: IBackendSearchGeneric<peopleAddress>) =>
        handleApiThenGeneric({
          res,
          notifFail: false,
          notifSuccess: false,
          onSuccessData: (data) => {
            if (data.length > 0) {
              setAddress(data[0]);
            }
          },
        })
      )
      .catch(() => {});
  };

  return (
    <div>
      <div className='transition-shadow w-full lg:w-[200px] duration-300 rounded-lg flex justify-center shadow-none bg-t-bg-color hover:shadow-lg border border-t-border-color-base'>
        {!address && !lockerAddress && peopleAddressTypeId === 1 && (
          <span className='flex items-center gap-1 p-2 cursor-pointer ' onClick={toggleModal}>
            <EnvironmentOutlined />
            آدرس فرستنده
          </span>
        )}

        {!address && !lockerAddress && peopleAddressTypeId == 2 && (isNormalOrder ? !client || packages[index].package.length <= 0 : packages[index].package.length <= 0) && (
          <TcTooltip title={isNormalOrder ? 'ابتدا بسته و فرد گیرنده خود را انتخاب کنید' : 'ابتدا بسته خود را انتخاب کنید'}>
            <span className='flex items-center gap-1 p-2 opacity-50 cursor-not-allowed '>
              <EnvironmentOutlined />
              آدرس گیرنده
            </span>
          </TcTooltip>
        )}
        {!address && !lockerAddress && peopleAddressTypeId == 2 && (isNormalOrder ? packages[index].package.length > 0 && client : packages[index].package.length > 0) && (
          <span className='flex items-center gap-1 p-2 cursor-pointer ' onClick={toggleModal}>
            <EnvironmentOutlined />
            آدرس گیرنده
          </span>
        )}

        {address && (
          <div className='w-full'>
            <div className='grid'>
              <div className='p-2 cursor-pointer' onClick={toggleModal}>
                <span className='flex gap-1 overflow-hidden w-[150px]'>
                  <EnvironmentOutlined />
                  {address.title}
                </span>
                <span className='block text-xs text-t-secondary-text-color'>
                  {address.address.provinceTitle}/{address.address.cityTitle}
                </span>
                <span className='text-xs line-clamp-3 text-t-secondary-text-color'>{address.address.fullAddress}</span>
              </div>

              {lockerAddress && (
                <div className='w-full'>
                  <div className='p-2 cursor-pointer' onClick={toggleModal}>
                    <span className='flex gap-1 overflow-hidden w-[150px]'>
                      <EnvironmentOutlined />
                      {lockerAddress.pudoId ? 'آدرس پودو' : 'آدرس لاکر'}
                    </span>
                    {lockerAddress.pudoId ? (
                      <>
                        <span className='block text-xs text-t-secondary-text-color'>پودو انتخاب شده</span>
                        <span className='text-xs line-clamp-3 text-t-secondary-text-color'>- شناسه پودو : </span>
                        <span className='text-xs line-clamp-3 text-t-secondary-text-color'>{lockerAddress.pudoId}</span>
                      </>
                    ) : (
                      <>
                        {lockerAddress.locker?.kiosk && (
                          <span className='block text-xs text-t-secondary-text-color'>
                            {lockerAddress.locker.kiosk.address}/{lockerAddress.locker.kiosk.description}
                          </span>
                        )}
                        <span className='text-xs line-clamp-3 text-t-secondary-text-color'>- شماره سفارش : </span>
                        <span className='text-xs line-clamp-3 text-t-secondary-text-color'>{lockerAddress.orderNumber}</span>
                      </>
                    )}
                  </div>
                  {!lockerAddress.pudoId && lockerAddress?.locker?.kiosk?.lat && (
                    <div
                      onClick={() => setSelectedLocation({ lat: lockerAddress.locker!.kiosk!.lat, lng: lockerAddress.locker!.kiosk!.lon })}
                      className='flex items-center justify-end px-2 mt-2 mb-1 text-[10px] cursor-pointer text-t-secondary-color inherit-color'>
                      <EyeOutlined className='pl-1' />
                      مشاهده لوکیشن
                    </div>
                  )}

                  {/*    <hr className='my-1' /> <div className='flex items-center justify-center gap-2 py-1'>
                    <Popconfirm title='حذف شود؟' onConfirm={handleDeleteLockerAddress}>
                      <span className='block text-xs text-center cursor-pointer text-t-error-color inherit-color'>حذف</span>
                    </Popconfirm>
                    <span className='block text-xs text-center cursor-pointer text-t-secondary-color inherit-color' onClick={toggleModal}>
                      ویرایش
                    </span>
                  </div> */}
                </div>
              )}
            </div>
            {useLocker && peopleAddressTypeId !== 1 && !lockerAddress && (
              <div className='p-2 cursor-pointer'>
                <div
                  className='py-1 text-center rounded-lg bg-t-secondary-color hover:bg-t-hovered-secondary-color'
                  onClick={() => setShowLockerReservation(address.address.cityId)}>
                  {/* <FcViewDetails /> */}
                  <span className='text-[13px] text-t-opposite-text-color'> +‌‌ انتخاب لاکر/پودو</span>
                </div>
              </div>
            )}
            <hr className='my-1' />
            <div className='flex items-center justify-center gap-2 py-1'>
              {lockerAddress && (
                <Popconfirm title='حذف شود؟' onConfirm={handleDeleteLockerAddress}>
                  <span className='block text-xs text-center cursor-pointer text-t-error-color inherit-color'>{lockerAddress.pudoId ? 'حذف پودو' : 'حذف لاکر'}</span>
                </Popconfirm>
              )}
              {!lockerAddress && peopleAddressTypeId !== 1 && (
                <Popconfirm title='حذف شود؟' onConfirm={handleDelete}>
                  <span className='block text-xs text-center cursor-pointer text-t-error-color inherit-color'>حذف</span>
                </Popconfirm>
              )}
              {peopleAddressTypeId == 1 && (
                <Popconfirm title='حذف شود؟' onConfirm={handleDelete}>
                  <span className='block text-xs text-center cursor-pointer text-t-error-color inherit-color'>حذف</span>
                </Popconfirm>
              )}
              <span className='block text-xs text-center cursor-pointer text-t-secondary-color inherit-color' onClick={toggleModal}>
                ویرایش
              </span>
            </div>
          </div>
        )}
      </div>
      <OrderAddressPicker
        close={toggleModal}
        visible={showModal}
        setAddress={setAddress}
        peopleAddressTypeId={peopleAddressTypeId}
        packages={packages}
        index={index}
        setLockerAddress={setLockerAddress}
        client={client}
        isNormalOrder={isNormalOrder}
      />

      <TcShowLocationModal location={selectedLocation} close={() => setSelectedLocation(undefined)} />

      <OrderSelectLocker
        visible={showLockerReservation}
        close={() => setShowLockerReservation(undefined)}
        packages={packages}
        index={index}
        setLockerAddress={setLockerAddress}
        lockerAddress={lockerAddress}
      />
    </div>
  );
};

export default OrderAddressBox;
